// 국양건축사사무소 30주년 꽃가루 (2026-06-19 하루 한정).
//   - 대시보드(/dashboard/*)에 진입할 때, 그리고 fireConfetti() 호출 시 8초(CONFETTI_MS) 버스트.
//   - pointer-events: none — 클릭/입력을 절대 막지 않는다.
//   - 모달(z-index 1300)보다 뒤(1200)에 깔려 모달 가독성을 해치지 않는다.
//   - 새 라이브러리 없이 CSS 애니메이션만 사용.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import { keyframes } from '@mui/system';

import { ANNIVERSARY } from 'config';

// 외부(예: 모달 닫기)에서 버스트를 발사하는 트리거 — window 이벤트로 느슨하게 결합
const CONFETTI_EVENT = 'kukyang:confetti';
export function fireConfetti(): void {
  try {
    window.dispatchEvent(new Event(CONFETTI_EVENT));
  } catch {
    /* 이벤트 미지원 환경이면 그냥 무시 */
  }
}

const fall = keyframes`
  0%   { transform: translateY(-12vh) rotate(0deg); opacity: 0; }
  8%   { opacity: 1; }
  100% { transform: translateY(110vh) rotate(720deg); opacity: 0.95; }
`;

// 골드 위주 + 축제 포인트 컬러
const COLORS = ['#C8A24B', '#9C7A2E', '#E9CF8A', '#E0533D', '#3D7BE0', '#3DAE6B', '#E07FB0'];

const PIECES = 70;
const DASHBOARD_PREFIX = '/dashboard'; // 이 경로 진입마다 재발사

interface Piece {
  left: number;
  size: number;
  height: number;
  color: string;
  duration: number;
  delay: number;
  round: boolean;
}

function active(): boolean {
  if (!ANNIVERSARY.ENABLED || !ANNIVERSARY.CONFETTI) return false;
  return dayjs().format('YYYY-MM-DD') === ANNIVERSARY.DATE;
}

function genPieces(): Piece[] {
  return Array.from({ length: PIECES }, () => {
    const size = 6 + Math.random() * 8;
    return {
      left: Math.random() * 100, // vw
      size,
      height: size * (0.4 + Math.random() * 0.8),
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      duration: 4 + Math.random() * 3, // s
      delay: Math.random() * 2.5, // s
      round: Math.random() > 0.6
    };
  });
}

export default function AnniversaryConfetti() {
  const { pathname } = useLocation();
  const [run, setRun] = useState<{ id: number; pieces: Piece[] } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 8초 버스트 1회 발사 (재호출 시 이전 타이머를 정리하고 새로 시작)
  const fire = useCallback(() => {
    if (!active()) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setRun((prev) => ({ id: (prev?.id ?? 0) + 1, pieces: genPieces() }));
    timerRef.current = setTimeout(() => setRun(null), ANNIVERSARY.CONFETTI_MS);
  }, []);

  // 대시보드 진입(경로 변경)마다 발사. 다른 화면으로 나가면 멈춘다.
  useEffect(() => {
    if (active() && pathname.startsWith(DASHBOARD_PREFIX)) {
      fire();
    } else {
      if (timerRef.current) clearTimeout(timerRef.current);
      setRun(null);
    }
  }, [pathname, fire]);

  // 외부 트리거(모달 닫기 등)에 반응
  useEffect(() => {
    const onFire = () => fire();
    window.addEventListener(CONFETTI_EVENT, onFire);
    return () => window.removeEventListener(CONFETTI_EVENT, onFire);
  }, [fire]);

  // 언마운트 시 타이머 정리
  useEffect(() => () => void (timerRef.current && clearTimeout(timerRef.current)), []);

  if (!run) return null;

  return (
    <Box
      key={run.id} // 재진입 시 remount → 애니메이션 깔끔히 재시작
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}
    >
      {run.pieces.map((p, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: 0,
            left: `${p.left}vw`,
            width: p.size,
            height: p.height,
            bgcolor: p.color,
            borderRadius: p.round ? '50%' : '1px',
            opacity: 0,
            animation: `${fall} ${p.duration}s linear ${p.delay}s forwards`
          }}
        />
      ))}
    </Box>
  );
}
